import type { Extensions, JSONContent } from '@tiptap/core';
import { generateHTML, generateText } from '@tiptap/core';
import { COTERA_RICH_CLIPBOARD_ATTR } from '@cotera/client/app/components/app/rte/lib/normalize-pasted-html';
import type { DG } from '@cotera/sdk/core';
import { StarterKit } from '@tiptap/starter-kit';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Highlight } from '@tiptap/extension-highlight';
import { Superscript } from '@tiptap/extension-superscript';
import { Subscript } from '@tiptap/extension-subscript';
import {
  CoteraEmoji,
  CoteraMention,
} from '@cotera/client/app/components/ui/tiptap/extensions';
import { HorizontalRule } from '@cotera/client/app/components/ui/tiptap/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension';

export type MentionClickData = {
  t: string;
  name: string;
  label?: string;
  /** Tool key for tool mentions (e.g. "heyreach/campaigns/addLeads" or "coco/agent-xxx") */
  toolKey?: string;
  /** Resource / tool id from the mention node's `referenceId` attr when present. */
  referenceId?: string;
  data?: {
    toolId?: string;
    toolName?: string;
    def?: { toolId?: string };
    /** Workspace resource @ mention (agents, chats, documents, triggers, …) */
    resourceType?: string;
    resourceId?: string;
  };
};

export type ReadonlyEditorExtensionConfig = {
  /** When provided, mention chips are clickable and invoke this handler. */
  onMentionClick?: (data: MentionClickData) => void;
  showMentionOwnershipIndicator?: boolean;
  mentionOwnershipTooltipContent?: string;
};

/**
 * The TipTap extension set used to render readonly chat message content. Shared
 * between `ReadonlyEditorState` (live rendering) and the message copy handler
 * (`generateHTML`/`generateText` serialization) so the two stay in exact parity —
 * otherwise a mention/emoji node could render in the message but fail to
 * serialize (or round-trip) on copy.
 */
export const readonlyEditorExtensions = (
  config: ReadonlyEditorExtensionConfig = {}
): Extensions => [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    horizontalRule: false,
  }),
  HorizontalRule,
  Color,
  TextStyle,
  Highlight.configure({ multicolor: true }),
  Superscript,
  Subscript,
  CoteraMention.configure({
    onClick: (data) => {
      config.onMentionClick?.(data);
    },
    showOwnershipIndicator: config.showMentionOwnershipIndicator ?? false,
    ownershipTooltipContent: config.mentionOwnershipTooltipContent,
  }),
  CoteraEmoji,
];

/**
 * Render inline content (text, mentions, emoji, …) via TipTap so mentions come
 * out as a clean `@label` and emoji render — identical to how a plain paragraph
 * serializes. Wrapping in a one-paragraph doc means `generateText` returns just
 * the inline text with no surrounding block separators.
 */
const renderInline = (
  content: JSONContent[] | undefined,
  extensions: Extensions
): string => {
  if (content === undefined || content.length === 0) {
    return '';
  }
  return generateText(
    { type: 'doc', content: [{ type: 'paragraph', content }] },
    extensions
  );
};

/**
 * Prefix a list item's first line with its marker (`1. ` / `- `) and indent any
 * continuation lines (nested lists, wrapped multi-paragraph items) so they line
 * up under the text rather than the marker.
 */
const withMarker = (marker: string, body: string): string => {
  const indent = ' '.repeat(marker.length);
  const [first = '', ...rest] = body.split('\n');
  if (rest.length === 0) {
    return `${marker}${first}`;
  }
  return [`${marker}${first}`, ...rest.map((line) => `${indent}${line}`)].join(
    '\n'
  );
};

function renderList(list: JSONContent, extensions: Extensions): string {
  const ordered = list.type === 'orderedList';
  const startAttr: unknown = list.attrs?.['start'];
  const start = typeof startAttr === 'number' ? startAttr : 1;
  return (list.content ?? [])
    .map((item, index) => {
      const marker = ordered ? `${start + index}. ` : '- ';
      const body = (item.content ?? [])
        .map((child) => renderBlock(child, extensions))
        .join('\n');
      return withMarker(marker, body);
    })
    .join('\n');
}

function renderBlock(node: JSONContent, extensions: Extensions): string {
  switch (node.type) {
    case 'bulletList':
    case 'orderedList':
      return renderList(node, extensions);
    case 'blockquote':
      return (node.content ?? [])
        .map((child) => renderBlock(child, extensions))
        .join('\n\n');
    default:
      // paragraph / heading / codeBlock / … — a text block whose inline
      // children (incl. mentions & emoji) render via TipTap.
      return renderInline(node.content, extensions);
  }
}

/**
 * Serialize an editor state to plain text, preserving list markers (`1. `,
 * `- `). TipTap's own `generateText` flattens list items to bare text and drops
 * the numbers/bullets, which loses the structure on copy — so we walk the
 * blocks ourselves and only defer inline runs to `generateText`.
 */
const editorStateToPlainText = (
  doc: JSONContent,
  extensions: Extensions
): string =>
  (doc.content ?? []).map((node) => renderBlock(node, extensions)).join('\n\n');

/**
 * Serializes a readonly chat message editor state into the two clipboard flavors
 * used by the message Copy button:
 *
 * - `html`: the mention spans (`<span data-type="mention" …>`), so pasting back
 *   into a Coco composer round-trips references into real pills.
 * - `text`: a clean `@label` rendering (via each mention's `renderText`) for
 *   non-Coco paste targets, with list markers preserved.
 *
 * Deliberately does NOT use `toPromptMarkdown`, which is the LLM-prompt serializer
 * and renders tools as `` `snake_name` (tool) `` — see COT-6598.
 */
export const serializeEditorStateForClipboard = (
  editorState: DG.DocumentEditor.DocumentState
): { html: string; text: string } => {
  const extensions = readonlyEditorExtensions();
  return {
    // The wrapper marks this as internal Coco content so a paste back into a
    // composer preserves the rich structure instead of flattening it (see
    // `transformExternalPastedHtml`). It's inert for external paste targets.
    html: `<div ${COTERA_RICH_CLIPBOARD_ATTR}="true">${generateHTML(
      editorState,
      extensions
    )}</div>`,
    text: editorStateToPlainText(editorState, extensions),
  };
};
